import React, { ReactElement, useState, useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import Head from 'next/head';
import { useQuery } from '@apollo/client';
import axios, { AxiosError } from 'axios';
import { noticeError } from 'lib/newrelic';
import { getViewMode } from 'lib/helpers';
import { isServer } from 'lib/isBrowser';
import { trackClick } from 'lib/tealium/provider';
import SeriesModel from 'models/series.model';
import { IGetInitialProps } from 'typings/IGetInitialProps';
import { IFormatsByFilter } from 'typings/IFormatsByFilter';
import { IWithInitialProps } from 'typings/IWithInitialProps';
import useFeature from 'hooks/useFeature';
import OverviewMetadataQuery from 'queries/overview/overviewMetadata.query';
import { SkeletonAToZFormats } from 'components/SkeletonElement/SkeletonElement';
import featureTooling from 'components/FeatureTooling/FeatureTooling';
import { FormatAToZStyle, FormatAToZPickerStyle } from 'components/FormatAToZ/FormatAToZ.style';
import Picker from 'components/Picker';
import FormatsSection from 'components/FormatAToZ/FormatsSectionTileList';
import FormatsSectionCarousel from 'components/FormatAToZ/FormatsSectionCarousel';
import ErrorMessage from 'components/ErrorMessage';
import { Grid, GridElement } from '../../src/client/styles/grid';
import {
  FEATURE_SLUG,
  PROMO_QUERY_CLIENT,
  META_DESCRIPTION_CONTENT,
  META_TITLE_CONTENT,
  TEALIUM_EVENT_NAME,
  TEALIUM_EVENT_CATEGORY,
  AVAILABLE_REGIONS
} from '../../src/client/constants';

const ALPHABET = 'abcdefghijklmnopqrstuvwxyz';
const ALL = 'alle';
const A_TO_Z = 'a-z';
const PROGRAMMAS_SLUG = 'programmas';

const findFormatsByLetter = (formats: SeriesModel[], letter: string): SeriesModel[] => {
  const Regex: RegExp = new RegExp(letter);
  return formats
    .filter(format => {
      // find index of first alphanumeric char in title
      const Index = format.sortTitle.toLowerCase().search(/[a-z0-9]/);
      return format.sortTitle.charAt(Index).toLowerCase().match(Regex);
    })
    .map((format, index) => ({
      ...format,
      eventValues: {
        ...format.eventValues,
        label: letter,
        value: index + 1
      }
    }));
};

// count number of formats shown in previous filter sections because of section_item_index, for specific filter
const countNoOfShownFormatsForFilter = (formatsByFilter: IFormatsByFilter, filter: string): number => {
  let count = 0;
  const filtersKeys = Object.keys(formatsByFilter);
  for (const filterKey of filtersKeys) {
    if (filterKey !== filter) {
      count += formatsByFilter[filterKey].length;
    } else {
      break;
    }
  }
  return count;
};
let seriesCache: ISeriesGraphql[] | null = null;

const useSeriesData = ({
  initialSeries,
  seriesJson
}: {
  initialSeries?: ISeriesGraphql[];
  seriesJson: string;
}): { loading: boolean; error?: boolean; series: SeriesModel[] } => {
  const [state, setState] = React.useState<{ loading: boolean; error?: boolean; series: SeriesModel[] }>(() => {
    if (initialSeries) seriesCache = initialSeries;

    if (seriesCache) return { loading: false, series: seriesCache.map(item => new SeriesModel(item)) };
    return { loading: true, series: [] };
  });

  React.useEffect(() => {
    if (state.series.length) return;

    const fetchSeries = async (disableCache = false): Promise<SeriesModel[] | null> => {
      try {
        const { data } = await axios.get<ISeriesGraphql[]>(
          `${seriesJson}${disableCache ? `?timestamp=${new Date().getTime()}` : ''}`
        );
        if (data) seriesCache = data;
        return data.map(item => new SeriesModel(item));
      } catch (error) {
        const { config, message } = error as AxiosError;
        noticeError(`Could not fetch ${config?.url || 'series'} | ${message}`);
      }
      return null;
    };

    fetchSeries().then(series => {
      if (series) return setState({ series, loading: false });
      // retry with bypassing CloudFront cache
      fetchSeries(true).then(series => {
        if (series) return setState({ series, loading: false });
        setState({ loading: false, error: true, series: [] });
      });
    });
  }, []);

  return state;
};

const FormatAToZ: IWithInitialProps<IFormatAToZ> = ({ initialData, seriesJson, countryCode }): ReactElement => {
  const isInNL = countryCode === AVAILABLE_REGIONS.NETHERLANDS;
  const { enabled, variables } = useFeature<{ [key: string]: string[] }>(FEATURE_SLUG.GENRE_CATEGORIES);

  const isGenreOverview = (initialData?.isGenreOverview || enabled) && isInNL;
  const genreVariables = isInNL ? initialData?.genreVariables || variables : null;

  const {
    series: Formats,
    loading: loadingSeries,
    error
  } = useSeriesData({
    initialSeries: initialData?.formats,
    seriesJson
  });

  const [isHeaderHidden, setIsHeaderHidden] = useState<boolean>(false);
  const router = useRouter();
  const [selectedFilter, setSelectedFilter] = useState<string>(initialData?.selectedFilter || ALL);
  const [metadata, setMetadata] = useState<IEditorialOverviewData['metadata'] | null>(null);

  const { filters, formatsByLetter, genresData } = useMemo<{
    filters: IPickerItem[] | undefined;
    formatsByLetter: IFormatsByFilter;
    genresData: {
      formats: IFormatsByFilter;
      isGenrePicker: boolean;
    };
  }>(() => {
    const formatsByLetter: IFormatsByFilter = {};
    if (Formats?.length) {
      ALPHABET.split('').forEach(letter => {
        const formats = findFormatsByLetter(Formats, letter);
        if (formats.length > 0) {
          formatsByLetter[letter] = formats;
        }
      });

      // find titles that start with a number
      const formats = findFormatsByLetter(Formats, '[0-9]');
      if (formats.length > 0) {
        formatsByLetter['#'] = formats;
      }
    }

    if (!isGenreOverview) {
      const letters: IPickerItem[] = [
        { label: ALL },
        ...ALPHABET.split('').map(letter => ({ label: letter })),
        { label: '#' }
      ];

      letters.forEach((letter, index) => {
        const { label } = letter;
        if (label === ALL) return;
        const letterAvailable = Formats.some(item =>
          item.sortTitle?.match(new RegExp(`^${letter.label === '#' ? '[0-9]' : letter.label}`, 'i'))
        );
        letters[index].isDisabled = !letterAvailable;
      });
      return { formatsByLetter, filters: letters, genresData: { formats: {}, isGenrePicker: false } };
    }

    const genreFilters: IPickerItem[] = [{ label: ALL, path: '/programmas' }];
    const genreFormats: IFormatsByFilter = {};
    genreVariables &&
      Object.keys(genreVariables).map((key, index) => {
        const formats: SeriesModel[] = [];
        if (genreVariables[key].length) genreFilters[index + 1] = { label: key, path: `?genre=${key.toLowerCase()}` };
        genreVariables[key].map((value: string) => {
          const format = Formats?.find(({ guid }) => guid === value);
          if (format) {
            formats.push({
              ...format,
              eventValues: {
                ...format.eventValues,
                label: key,
                value: formats.length + 1
              }
            });
          }
        });
        if (formats.length) genreFormats[key] = formats;
      });
    genreFilters.push({ label: A_TO_Z, path: `?genre=${A_TO_Z}` });
    return { formatsByLetter, filters: genreFilters, genresData: { formats: genreFormats, isGenrePicker: true } };
  }, [genreVariables, Formats]);

  useEffect(() => {
    let ScrollPosition: number = window.scrollY;
    const handleScroll = (): void => {
      setIsHeaderHidden(window.scrollY > 80 && window.scrollY >= ScrollPosition);
      ScrollPosition = window.scrollY;
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useQuery(OverviewMetadataQuery, {
    variables: {
      slug: PROGRAMMAS_SLUG,
      client: PROMO_QUERY_CLIENT.DESKTOP
    },
    skip: !!metadata,
    onCompleted: (data: { overview?: IEditorialOverviewData }) => {
      if (data?.overview?.metadata) {
        setMetadata(data.overview.metadata);
      }
    }
  });

  const formatsCount = useMemo(
    () =>
      [...Object.entries(genresData.formats), ...Object.entries(formatsByLetter)].reduce(
        (acc, [filter]) => ({
          ...acc,
          [filter]: countNoOfShownFormatsForFilter(
            formatsByLetter[filter] ? formatsByLetter : genresData.formats,
            filter
          )
        }),
        {} as { [key: string]: number }
      ),

    [formatsByLetter, genresData]
  );

  useEffect(() => {
    if (!genreVariables) return;
    const { genre } = router.query;

    const target = genre === A_TO_Z ? A_TO_Z : Object.keys(genreVariables).find(item => item.toLowerCase() === genre);

    setSelectedFilter(target || ALL);
  }, [router]);

  const handleItemClick = (filter: string, isGenreMoreClick: boolean = false): void => {
    isGenreMoreClick
      ? trackClick({
          name: TEALIUM_EVENT_NAME.CONTENT_LISTING_MORE,
          category: TEALIUM_EVENT_CATEGORY.CONTENT_LISTING,
          label: `${filter}`
        })
      : trackClick({
          name: TEALIUM_EVENT_NAME.MENU_CLICK,
          category: TEALIUM_EVENT_CATEGORY.MENU,
          label: `genre | ${filter}`
        });
    if (filter !== selectedFilter) {
      // add genre to URL
      if (genresData.isGenrePicker) {
        return;
      }
      setSelectedFilter(filter);
      window.scrollTo(0, 0);
    }
  };
  if (loadingSeries || !filters) return <SkeletonAToZFormats />;
  if (!Formats?.length || error) {
    return (
      <FormatAToZStyle>
        <ErrorMessage />
      </FormatAToZStyle>
    );
  }

  return (
    <>
      <Head>
        <title>{metadata?.title || META_TITLE_CONTENT.PROGRAMMAS}</title>
        <meta name="og:description" content={metadata?.description || META_DESCRIPTION_CONTENT.PROGRAMMAS} />
        <meta name="description" content={metadata?.description || META_DESCRIPTION_CONTENT.PROGRAMMAS} />
      </Head>
      <FormatAToZStyle data-testid="formatAToZ">
        <FormatAToZPickerStyle isHeaderHidden={isHeaderHidden}>
          <Picker
            items={filters}
            isGenrePicker={genresData.isGenrePicker}
            selectedItem={selectedFilter}
            handlePickerItemClick={handleItemClick}
          />
        </FormatAToZPickerStyle>
        <Grid>
          <GridElement smColumn="1 / 13" mdColumn="1 / 13" lgColumn="1 / 13">
            {((selectedFilter === ALL && !genresData.isGenrePicker) ||
              (selectedFilter === A_TO_Z && genresData.isGenrePicker)) &&
              Object.entries(formatsByLetter).map(([letter, formats]) => (
                <FormatsSection
                  filter={letter}
                  formats={formats}
                  key={letter}
                  noOfShownItemsAbove={formatsCount[letter]}
                />
              ))}
            {selectedFilter === ALL &&
              genresData.isGenrePicker &&
              Object.entries(genresData.formats).map(([genre, formats]) => (
                <FormatsSectionCarousel
                  title={genre}
                  formats={formats}
                  key={genre}
                  handleTitleClick={handleItemClick}
                  noOfShownItemsAbove={formatsCount[genre]}
                />
              ))}
            {selectedFilter !== ALL && selectedFilter !== A_TO_Z && (
              <FormatsSection
                filter={selectedFilter}
                formats={
                  genresData.isGenrePicker ? genresData.formats[selectedFilter] : formatsByLetter[selectedFilter]
                }
                noOfShownItemsAbove={0}
              />
            )}
          </GridElement>
        </Grid>
      </FormatAToZStyle>
    </>
  );
};

FormatAToZ.getInitialProps = async ({
  req,
  query,
  apolloClient,
  seriesJson,
  countryCode
}: IGetInitialProps): Promise<any> => {
  if (!isServer || !seriesJson) return {};

  const { data: series } = await axios.get<ISeriesGraphql[]>(seriesJson);

  if (!series) return {};

  await featureTooling.init(process.env.DATA_FILE_URL as string, getViewMode({ req }));

  if (countryCode !== AVAILABLE_REGIONS.NETHERLANDS) {
    return {
      initialData: {
        formats: series,
        isGenreOverview: false
      }
    };
  }

  try {
    await apolloClient.query<{ overview: { metadata: IOverviewMetadata } }>({
      query: OverviewMetadataQuery,
      variables: {
        slug: PROGRAMMAS_SLUG,
        client: PROMO_QUERY_CLIENT.DESKTOP
      }
    });
  } catch (ignore) {}

  const genreVariables = featureTooling.getFeatureVariables<{ [key: string]: string[] }>(FEATURE_SLUG.GENRE_CATEGORIES);
  const selectedFilter = genreVariables && Object.keys(genreVariables).find(item => item.toLowerCase() === query.genre);

  return {
    initialData: {
      genreVariables,
      selectedFilter,
      formats: series,
      isGenreOverview: !!genreVariables
    }
  };
};
export default FormatAToZ;
